import castArray from 'lodash/castArray';
import camelCase from 'lodash/fp/camelCase';
import flow from 'lodash/fp/flow';
import isNil from 'lodash/fp/isNil';
import mapKeys from 'lodash/fp/mapKeys';
import omitBy from 'lodash/fp/omitBy';
import pickBy from 'lodash/fp/pickBy';
import isEmpty from 'lodash/isEmpty';
import toLower from 'lodash/toLower';

import { paramsParser } from '@lumapps/base-api';
import type { GetWidgetURLFiltersByTypeOptions } from '@lumapps/widget-base/hooks/useWidgetURLFilters';

import { GenericListFilterType, URLGenericListFilterEnum } from '../types';
import { extractArrayFilterV1Values } from './extractArrayFilterV1Values';

interface URLGenericListFiltersOptionType extends GetWidgetURLFiltersByTypeOptions {}

/**
 * Get the filters from the URL, filters by existing filters and clean the values when its possible
 * The expected URL format is the following: ?filters[filter1]=value1&filters[filter1]=value2&filters[filter2]=value3
 * It can optionally try to interpret v1 url filter format if the option is enabled
 * @returns object containing filters and their values
 */
export const getReadGenericListURLFilters = (
    search: string,
    options: URLGenericListFiltersOptionType = {},
): Partial<GenericListFilterType> => {
    const { isLegacyCompatible = false } = options;

    /**
     * Legacy filter parser
     */
    if (/filters=/.test(search) && isLegacyCompatible) {
        const params = new URLSearchParams(search);
        const filters = params.getAll('filters');

        /** Extracting metadata */
        const metadataIds = extractArrayFilterV1Values(filters, 'metadata');

        /** Extracting tags */
        const tagIds = extractArrayFilterV1Values(filters, 'tags');

        /** Extracting the first searchQuery */
        const searchQuery = filters.find((filter) => filter.startsWith('query_'));

        /** Extract highlighted for me value */
        const onlyHighlighted =
            filters.find((filter) => filter.startsWith('onlyFeatureFeeds_'))?.split('_')[1] === 'true';

        /** Extracting the start date */
        const startDate = filters.find((filter) => filter.startsWith('startDate_'));

        /** Extracting the end date */
        const endDate = filters.find((filter) => filter.startsWith('endDate_'));

        return omitBy(isNil, {
            searchQuery: searchQuery ? searchQuery.split('_')[1] : undefined,
            metadata: !isEmpty(metadataIds)
                ? (metadataIds as string[]).map((metadataId) => ({ id: metadataId }))
                : undefined,
            tags: !isEmpty(tagIds) ? (tagIds as string[]).map((tagId) => ({ id: tagId })) : undefined,
            onlyHighlighted,
            publicationDates:
                (startDate || endDate) &&
                omitBy(isNil, {
                    startDate,
                    endDate,
                }),
        });
    }

    /**
     * NGI filter parser
     */
    const { filters: rawFilters } = paramsParser(search, { ignoreQueryPrefix: true });

    if (!rawFilters) {
        return {};
    }

    const {
        searchQuery,
        metadataIds,
        ignoredDefaultFilters,
        tagsIds,
        tab,
        onlyHighlighted,
        startDate,
        endDate,
        author,
        siteIds,
    } = flow(
        pickBy((value, key) => key in URLGenericListFilterEnum),
        mapKeys(camelCase),
    )(rawFilters);

    return omitBy(isNil, {
        ignoredDefaultFilters: !isEmpty(ignoredDefaultFilters)
            ? castArray(ignoredDefaultFilters).map(toLower)
            : undefined,
        searchQuery: searchQuery as string | undefined,
        metadata: !isEmpty(metadataIds)
            ? (castArray(metadataIds) as string[]).map((metadataId) => ({ id: metadataId }))
            : undefined,
        site: !isEmpty(siteIds) ? (castArray(siteIds) as string[]).map((siteId) => ({ id: siteId })) : undefined,
        tags: !isEmpty(tagsIds) && !tab ? (castArray(tagsIds) as string[]).map((tagId) => ({ id: tagId })) : undefined,
        onlyHighlighted: onlyHighlighted ? onlyHighlighted === 'true' : undefined,
        publicationDates:
            (startDate || endDate) &&
            omitBy(isNil, {
                startDate,
                endDate,
            }),
        author: author && {
            email: author,
        },
    });
};
